import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { toast } from 'vue-sonner';
import { clientReengagementDays, clientShouldAlertOnInvoiceOverdue, clientShouldPromptOnWon } from '~/lib/clientLifecycleAutomation';
import type { Client, ClientLifecycle } from '~/types/models';
import type { ClientAutomationDraft } from '@/components/clients/automation/types';

export function useClientWorkspaceAutomation(options: {
    client: MaybeRefOrGetter<Client | null>;
    clientCurrency: MaybeRefOrGetter<string>;
    updateLifecycle: (patch: Partial<ClientLifecycle>) => Promise<void>;
}) {
    const revenueSaving = ref(false);
    const automationSaving = ref(false);
    const automationDraft = ref<ClientAutomationDraft>({
        promptOnWon: true,
        alertOnInvoiceOverdue: true,
        reengageAfterInactiveDays: '90',
    });

    const automationSettings = computed(() => ({
        promptOnWon: clientShouldPromptOnWon(toValue(options.client)),
        alertOnInvoiceOverdue: clientShouldAlertOnInvoiceOverdue(toValue(options.client)),
        reengageAfterInactiveDays: clientReengagementDays(toValue(options.client)),
    }));
    const revenueTarget = computed(() => {
        const target = toValue(options.client)?.lifecycle?.annualValueTarget;
        return target == null ? '' : String(target);
    });
    const revenueCurrency = computed(() => (
        String(toValue(options.client)?.lifecycle?.annualValueCurrency || '').trim()
        || toValue(options.clientCurrency)
    ));

    watch(
        () => toValue(options.client),
        () => {
            automationDraft.value = {
                promptOnWon: automationSettings.value.promptOnWon,
                alertOnInvoiceOverdue: automationSettings.value.alertOnInvoiceOverdue,
                reengageAfterInactiveDays: automationSettings.value.reengageAfterInactiveDays == null
                    ? ''
                    : String(automationSettings.value.reengageAfterInactiveDays),
            };
        },
        { immediate: true },
    );

    async function saveRevenue(targetInput?: string, currencyInput?: string) {
        if (!toValue(options.client)?.id) {
            toast.error('Client not found.');
            return;
        }
        revenueSaving.value = true;
        try {
            const nextTarget = Number((targetInput ?? revenueTarget.value) || 0);
            await options.updateLifecycle({
                annualValueTarget: Number.isFinite(nextTarget) && nextTarget > 0 ? nextTarget : undefined,
                annualValueCurrency: String(currencyInput || revenueCurrency.value || toValue(options.clientCurrency)).trim().toUpperCase(),
            });
            toast.success('Revenue intelligence updated.');
        } catch (error: any) {
            toast.error(error?.message || 'Could not update revenue intelligence.');
        } finally {
            revenueSaving.value = false;
        }
    }

    async function saveSettings(draftInput?: ClientAutomationDraft) {
        if (!toValue(options.client)?.id) {
            toast.error('Client not found.');
            return;
        }
        automationSaving.value = true;
        try {
            const draft = draftInput || automationDraft.value;
            const reengagementDays = String(draft.reengageAfterInactiveDays || '').trim();
            await options.updateLifecycle({
                automationSettings: {
                    promptOnWon: draft.promptOnWon,
                    alertOnInvoiceOverdue: draft.alertOnInvoiceOverdue,
                    reengageAfterInactiveDays: reengagementDays === '' ? null : Math.max(1, Number(reengagementDays) || 0),
                    updatedAt: new Date(),
                },
            });
            toast.success('Automation settings updated.');
        } catch (error: any) {
            toast.error(error?.message || 'Could not update automation settings.');
        } finally {
            automationSaving.value = false;
        }
    }

    return {
        revenueSaving,
        automationSaving,
        automationDraft,
        automationSettings,
        revenueTarget,
        revenueCurrency,
        saveRevenue,
        saveSettings,
    };
}
